<template>
  <div>
    <v-dialog
      max-width="720"
      v-model="isDialogOpen"
      @click:outside="closeDialog"
    >
      <v-card
        class="pa-4"
      >
        <page-title :value="`${$t('foodMenu.editTitle')} - ${formattedDate}`" />

        <div class="my-4">
          <div class="category-header">
            <div class="font-weight-bold my-2">{{ $t('foodMenu.selectCategory') }}</div>
            <v-spacer />
            <v-btn
              color="primary"
              text
              small
              @click="openCreateCategoryDialog"
            >+ {{ $t('foodMenu.addCategory') }}</v-btn>
          </div>
          <div>
            <div class="category-table-headers">
              <div class="category-table-header col-checkbox"></div>
              <div class="category-table-header col-name">{{ $t('foodMenu.categoryName') }}</div>
              <div class="category-table-header col-classes">{{ $t('foodMenu.targetClasses') }}</div>
            </div>
            <div 
              class="category-table-row"
              v-for="(c, i) in menuCategories"
              :key="i"
              @click="() => categoryId = c.categoryId"
            >
              <div class="category-table-item col-checkbox">
                <v-icon v-if="categoryId === c.categoryId" color="primary">mdi-checkbox-marked</v-icon>
                <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              </div>
              <div class="category-table-item col-name"> {{ c.name }} </div>
              <div class="category-table-item col-classes"> {{ formatClassNames(c.targetClasses) }} </div>
            </div>
          </div>
          <v-divider />
        </div>

        <div class="my-4">
          <v-textarea
            v-model="body"
            variant="outlined"
            :label="$t('foodMenu.menu')"
            hide-details
          />
        </div>
        <div class="my-4">
          <v-textarea
            v-model="note"
            variant="outlined"
            :label="$t('foodMenu.note')"
            hide-details
          />
        </div>
        <div class="my-4">
          <v-select
            v-model="allergenIds"
            variant="outlined"
            hide-details
            :label="$t('foodMenu.allergenFoods')"
            :items="allergenOptions"
            item-title="displayName"
            item-value="allergenId"
            multiple
          />
        </div>

        <v-progress-linear v-if="isUpdating" indeterminate color="primary" class="my-4" />

        <div class="my-4 d-flex align-center">
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            :disabled="isUpdating"
            @click="updateMenu"
          >{{ $t('common.save') }}</v-btn>
          <v-spacer />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';

import { foodMenusModule } from '@/store/viewModules/food-menu/foodMenusModule';
import dayjs from 'dayjs';
import 'dayjs/locale/en';

import PageTitle from '@/components/atoms/PageTitle.vue';
import { MealAllergenResponse, NurseryClassResponse } from 'chaild-api/lib';

interface DataType {
  isUpdating: boolean;
}

export default defineComponent({
  components: {
    PageTitle,
  },

  data: (): DataType => ({
    isUpdating: false,
  }),

  computed: {
    editingMenu() {
      return foodMenusModule.editingMenu;
    },
    categoryId: {
      get(): number | null {
        if (this.editingMenu) {
          return this.editingMenu.category.categoryId;
        }
        return null;
      },
      set(value: number) {
        if (this.editingMenu) {
          const menu = {...this.editingMenu};
          const category = this.menuCategories.find((c) => c.categoryId === value);
          if (category) {
            menu.category = category;
            foodMenusModule.setEditingMenu(menu);
          }
        }
      }
    },
    body: {
      get(): string | null {
        if (this.editingMenu) {
          return this.editingMenu.body;
        }
        return null;
      },
      set(value: string) {
        if (this.editingMenu) {
          const menu = {...this.editingMenu};
          menu.body = value;
          foodMenusModule.setEditingMenu(menu);
        }
      }
    },
    note: {
      get(): string | null {
        if (this.editingMenu) {
          return this.editingMenu.note;
        }
        return null;
      },
      set(value: string) {
        if (this.editingMenu) {
          const menu = {...this.editingMenu};
          menu.note = value;
          foodMenusModule.setEditingMenu(menu);
        }
      }
    },
    allergenIds: {
      get(): number[] | null {
        if (this.editingMenu) {
          return this.editingMenu.allergens.map((a) => a.allergenId);
        }
        return null;
      },
      set(value: number[]) {
        if (this.editingMenu) {
          const menu = {...this.editingMenu};
          const allergens = value.map((allergenId) => {
            const find = this.originalAllergens.find((a) => a.allergenId === allergenId);
            return find || null;
          }).filter((a) => a !== null) as MealAllergenResponse[];
          menu.allergens = allergens;
          foodMenusModule.setEditingMenu(menu);
        }
      }
    },
    isDialogOpen() {
      if (this.editingMenu) {
        return true;
      }
      return false;
    },
    menuCategories() {
      return foodMenusModule.menuCategories;
    },
    originalAllergens() {
      return foodMenusModule.allergens;
    },
    allergenOptions(): { allergenId: number; name: string; modifiable: boolean; displayName: string }[] {
      const allergenNameKeys: Record<string, string> = {
        '小麦': 'foodMenu.allergenNames.wheat',
        '大豆': 'foodMenu.allergenNames.soybean',
        '牛肉': 'foodMenu.allergenNames.beef',
        '卵': 'foodMenu.allergenNames.egg',
        '乳': 'foodMenu.allergenNames.milk',
        '落花生': 'foodMenu.allergenNames.peanut',
        '蕎麦': 'foodMenu.allergenNames.buckwheat',
        'エビ': 'foodMenu.allergenNames.shrimp',
        'カニ': 'foodMenu.allergenNames.crab',
        'サバ': 'foodMenu.allergenNames.mackerel',
        '豚肉': 'foodMenu.allergenNames.pork',
        '鶏肉': 'foodMenu.allergenNames.chicken',
        'ゼラチン': 'foodMenu.allergenNames.gelatin',
        'オレンジ': 'foodMenu.allergenNames.orange',
        'りんご': 'foodMenu.allergenNames.apple',
        '桃': 'foodMenu.allergenNames.peach',
        '山芋': 'foodMenu.allergenNames.yam',
        'いか': 'foodMenu.allergenNames.squid',
        'ごま': 'foodMenu.allergenNames.sesame',
        'くるみ': 'foodMenu.allergenNames.walnut',
        'あわび': 'foodMenu.allergenNames.abalone',
        'いくら': 'foodMenu.allergenNames.salmonRoe',
        'ｶｼｭｰﾅｯﾂ': 'foodMenu.allergenNames.cashewNut',
        'ｷｳｲﾌﾙｰﾂ': 'foodMenu.allergenNames.kiwiFruit',
        '鮭': 'foodMenu.allergenNames.salmon',
        'バナナ': 'foodMenu.allergenNames.banana',
        'まつたけ': 'foodMenu.allergenNames.matsutake',
        'タコ': 'foodMenu.allergenNames.octopus',
        'ホタテ': 'foodMenu.allergenNames.scallop',
        'あさり': 'foodMenu.allergenNames.clam',
      };
      return this.originalAllergens.map((allergen) => ({
        ...allergen,
        displayName: allergenNameKeys[allergen.name]
          ? (this.$t(allergenNameKeys[allergen.name]) as string)
          : allergen.name,
      }));
    },
    formattedDate(): string | null {
      if (this.editingMenu) {
        return dayjs(this.editingMenu.date).locale('en').format('DD/MM (ddd)');
      }
      return null;
    },
  },

  methods: {
    closeDialog() {
      foodMenusModule.setEditingMenu(null);
    },
    openCreateCategoryDialog() {
      foodMenusModule.setIsCreateCategoryDialogOpen(true);
    },
    formatClassNames(classes: NurseryClassResponse[]) {
      if (!classes) return '';
      return classes.map((c) => c.className).join(',');
    },
    async updateMenu() {
      this.isUpdating = true;
      await foodMenusModule.updateMenu();
      this.isUpdating = false;

      this.closeDialog();
    }
  }
});
</script>
<style lang="scss" scoped>
.category-header {
  padding: 0 8px;

  font-size: 14px;

  background-color: #f0f0f0;
  display: flex;
  align-items: center;
}

.category-table-headers {
  display: flex;
  align-items: center;
  
  padding: 4px;
  height: 30px;

  border-bottom: solid 1px #e0e0e0;
}

.category-table-header {
  font-size: 12px;
  font-weight: bold;
}

.category-table-row {
  display: flex;
  align-items: center;

  height: 40px;

  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }
}

.category-table-item {
  font-size: 14px;
}

.col-checkbox {
  width: 40px;
  padding-left: 8px;
}

.col-name {
  width: 160px;
}

.col-classes {
  flex: 1;
}
</style>
